import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search, AlertTriangle, Send, Plus, ChevronDown, ChevronRight, ChevronLeft,
  Key, Calendar, Edit2, Unlink, Check, X, Smartphone, Loader2, FileDown, Mail, Phone,
} from "lucide-react";
import { api } from "../api/client";
import FileDropzone from "../components/FileDropzone";
import { naira, formatDate } from "../data/format";
import { exportPlanPdf } from "../utils/planPdf";

const EXPIRY_FILTERS = [
  { value: "", label: "All plans" },
  { value: "1", label: "Expiring in 1 day" },
  { value: "7", label: "Expiring in 7 days" },
  { value: "14", label: "Expiring in 14 days" },
];

const ORDERING_OPTIONS = [
  { value: "", label: "Sort: Recently added" },
  { value: "last_payment_desc", label: "Last payment - newest first" },
  { value: "last_payment_asc", label: "Last payment - oldest first" },
  { value: "progress_desc", label: "Completion - highest first" },
  { value: "progress_asc", label: "Completion - lowest first" },
  { value: "balance_desc", label: "Balance - highest first" },
  { value: "price_desc", label: "Price - highest first" },
  { value: "price_asc", label: "Price - lowest first" },
  { value: "expiry_asc", label: "Expiry - soonest first" },
  { value: "expiry_desc", label: "Expiry - latest first" },
];

const SERIAL_STATUS_FILTERS = [
  { value: "", label: "All plans" },
  { value: "linked", label: "Linked to a serial" },
  { value: "unlinked", label: "Unlinked" },
];

const CODE_STATUS_FILTERS = [
  { value: "", label: "All serials" },
  { value: "missing", label: "No code yet" },
  { value: "has", label: "Has a code" },
];

const LINK_STATUS_FILTERS = [
  { value: "", label: "All serials" },
  { value: "linked", label: "Linked to a plan" },
  { value: "unlinked", label: "Not linked" },
];

const COMPLETION_FILTERS = [
  { value: "", label: "All plans" },
  { value: "uncompleted", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const CODE_TIER_FILTERS = [
  { value: "", label: "Everyone" },
  { value: "week", label: "1 week code (< ₦200k)" },
  { value: "1m", label: "1 month code (₦200k – ₦300k)" },
  { value: "2m", label: "2 month code (₦300k – ₦500k)" },
  { value: "3m", label: "3 month code (> ₦500k)" },
];

const EXPORT_FIELDS = [
  { key: "customer", label: "Customer" },
  { key: "equipment", label: "Equipment" },
  { key: "total", label: "Total" },
  { key: "progress", label: "Progress" },
  { key: "last_payment", label: "Last payment" },
  { key: "last_payment_amount", label: "Last payment amount" },
  { key: "duration", label: "Duration" },
  { key: "serials", label: "Serials" },
  { key: "code_expiry", label: "Code expiry" },
  { key: "eligible_code", label: "Eligible code" },
];
const DEFAULT_EXPORT_FIELDS = ["customer", "equipment", "total", "progress", "last_payment", "duration", "serials", "code_expiry"];

const LAST_PAYMENT_FILTERS = [
  { value: "", label: "Any time" },
  { value: "1d", label: "Paid in last 24 hours" },
  { value: "7d", label: "Paid in last 7 days" },
  { value: "90d_plus", label: "No payment in 3+ months" },
  { value: "180d_plus", label: "No payment in 6+ months" },
  { value: "never", label: "Never paid" },
];

const PAGE_SIZE = 20;

/* ---------- formatting helpers ---------- */

function expiryColor(days) {
  if (days === null || days === undefined) return "var(--ink-400)";
  if (days <= 1) return "var(--red-700, #dc2626)";
  if (days <= 7) return "var(--amber-700, #d97706)";
  return "var(--ink-600)";
}

function expiryLabel(days) {
  if (days === null || days === undefined) return "—";
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return "Expires today";
  return `${days}d left`;
}

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function agoLabel(iso) {
  const d = daysSince(iso);
  if (d === null) return "Never";
  if (d <= 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function durationLabel(m) {
  if (!m || m.duration_days == null) return "—";
  const d = m.duration_days;
  const text = d < 60 ? `${d}d` : `${Math.round(d / 30)}mo`;
  return m.is_completed ? text : `${text} so far`;
}

function buildSummary(plan) {
  const m = plan.metrics;
  if (!m) return "";
  const name = plan.display_name || plan.equipment_name;
  const parts = [
    `${plan.customer_name} bought ${name} on ${formatDate(m.date_purchased)} for ${naira(m.total_price)}.`,
  ];
  if (m.is_completed) {
    parts.push(
      `The plan is fully paid${m.completed_at ? ` (completed ${formatDate(m.completed_at)}` : ""}${
        m.completed_at ? `, ${durationLabel(m).replace(" so far", "")} after purchase)` : ""
      }.`
    );
  } else {
    parts.push(
      `${naira(m.total_paid)} has been paid (${m.percent}%) and ${naira(m.balance)} is still outstanding.`
    );
    parts.push(
      m.last_payment_at
        ? `The last confirmed payment was ${agoLabel(m.last_payment_at).toLowerCase()} (${formatDate(m.last_payment_at)}).`
        : "No payment has been confirmed yet."
    );
  }
  (plan.serials || []).forEach((s) => {
    const d = s.days_until_expiry;
    if (d === null || d === undefined) return;
    parts.push(
      d < 0
        ? `Code for ${s.serial_number} expired ${Math.abs(d)} day(s) ago.`
        : d === 0
        ? `Code for ${s.serial_number} expires today.`
        : `Code for ${s.serial_number} expires in ${d} day(s) (${formatDate(s.code_expiry)}).`
    );
  });
  return parts.join(" ");
}

/* ---------- small components ---------- */

function TableSkeleton({ columns = 6 }) {
  return (
    <div style={{ marginTop: 16, border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: "var(--grey-100, #f8fafc)" }}>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} style={{ padding: "12px 14px" }}>
                  <div className="pl-skeleton-bar" style={{ width: i === 0 ? "60%" : "40%", height: 10 }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, row) => (
              <tr key={row} style={{ borderTop: "1px solid var(--line)" }}>
                {Array.from({ length: columns }).map((_, col) => (
                  <td key={col} style={{ padding: "14px" }}>
                    <div className="pl-skeleton-bar" style={{ width: col === 0 ? "80%" : "55%", height: 12 }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpinIcon({ size = 14, color = "var(--blue-600)" }) {
  return <Loader2 size={size} color={color} style={{ animation: "pl-spin 0.8s linear infinite" }} />;
}

function ProgressBar({ percent = 0, width = 80, height = 6 }) {
  const p = Math.max(0, Math.min(100, percent));
  const color = p >= 100 ? "#16a34a" : p >= 50 ? "#2563eb" : "#d97706";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width, height, background: "#e2e8f0", borderRadius: height / 2, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ width: `${p}%`, height: "100%", background: color, transition: "width 0.2s ease" }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, minWidth: 34 }}>{p}%</span>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-500, #64748b)" }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="pl-toolbar-select">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ---------- excel import ---------- */

function ImportSerialsModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function send(commit) {
    const body = new FormData();
    body.append("file", file);
    body.append("commit", commit ? "true" : "false");
    setBusy(true);
    setError("");
    try {
      const res = await api.request("/serials/import/", { method: "POST", body });
      setPreview(res);
      if (commit) {
        setDone(true);
        onImported();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function pick(e) {
    setFile(e.target.files[0] || null);
    setPreview(null);
    setDone(false);
    setError("");
  }

  const nothingToDo = preview && preview.new === 0 && preview.codes_updated === 0 && preview.names_filled === 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Import serials from Excel</div>
          <button className="pl-btn pl-btn-ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ink-500)", marginTop: 6 }}>
          Upload the GNSS CODES workbook (an <b>equipment</b> sheet and/or a <b>codes</b> sheet). New serials are added with their client name and code. Serials already here only take the sheet's code if it expires <b>later</b> than the one they have; links to plans are never changed.
        </div>

        <div style={{ marginTop: 14 }}>
          <FileDropzone
            accept=".xlsx"
            onChange={pick}
            title="Choose the GNSS CODES workbook"
            hint=".xlsx with an equipment and/or codes sheet — or drag it here"
          />
        </div>

        {error && <div style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>}

        {preview && (
          <div style={{ marginTop: 14, padding: 12, background: "var(--grey-100, #f8fafc)", borderRadius: 10, fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700 }}>{done ? "Imported" : "Preview — nothing saved yet"}</div>
            <div>{preview.total_in_file} unique serials in file ({preview.with_code_in_file} with a code)</div>
            <div><b>{preview.new}</b> new ({preview.new_with_name} with a name, {preview.new_with_code} with a code)</div>
            <div><b>{preview.codes_updated}</b> existing serials get a newer code ({preview.codes_kept} keep theirs: same or later expiry)</div>
            {preview.names_filled > 0 && <div>{preview.names_filled} existing serials get an empty name filled</div>}
            <div>{preview.left_alone} existing left as they are</div>
            {!done && preview.new_sample.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--ink-600)" }}>
                {preview.new_sample.slice(0, 6).map((s) => (
                  <div key={s.serial_number}><span style={{ fontFamily: "monospace" }}>{s.serial_number}</span> {s.client_name || "—"}</div>
                ))}
                {preview.new > 6 && <div>…and {preview.new - 6} more</div>}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button className="pl-btn pl-btn-ghost" onClick={onClose}>{done ? "Close" : "Cancel"}</button>
          {!done && (
            <button className="pl-btn pl-btn-ghost" disabled={!file || busy} onClick={() => send(false)}>
              {busy && !preview ? "Reading..." : "Preview"}
            </button>
          )}
          {!done && preview && (
            <button className="pl-btn pl-btn-confirm" disabled={busy || nothingToDo} onClick={() => send(true)}>
              {busy ? "Importing..." : nothingToDo ? "Nothing to import" : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- export ---------- */

function ExportModal({ onClose, filters, total, codeTier }) {
  const [format, setFormat] = useState("xlsx");
  const [fields, setFields] = useState(new Set(DEFAULT_EXPORT_FIELDS));
  const [perSerial, setPerSerial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedAs, setSavedAs] = useState("");

  const serialOnly = fields.size === 1 && fields.has("serials");
  const hasSerials = fields.has("serials");
  const canExport = total > 0 && (format === "txt" || fields.size > 0);
  const tierLabel = codeTier ? CODE_TIER_FILTERS.find((o) => o.value === codeTier)?.label.replace(/\s*\(.*\)$/, "") : "";

  function toggle(key) {
    setSavedAs("");
    setFields((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function run() {
    setBusy(true);
    setError("");
    setSavedAs("");
    try {
      const name = await api.download("/plans/export/", {
        ...filters,
        format,
        fields: format === "xlsx" ? EXPORT_FIELDS.filter((f) => fields.has(f.key)).map((f) => f.key).join(",") : undefined,
        per_serial: format === "xlsx" && hasSerials && (perSerial || serialOnly) ? "true" : undefined,
      });
      setSavedAs(name);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const seg = (active) => ({
    flex: 1, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left",
    border: `2px solid ${active ? "#15803d" : "var(--line)"}`, background: active ? "#f0fdf4" : "#fff",
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Export plans</div>
          <button className="pl-btn pl-btn-ghost" onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 10, background: "var(--grey-100, #f8fafc)", fontSize: 13 }}>
          Exports all <b>{total}</b> plan{total === 1 ? "" : "s"}{tierLabel ? <> eligible for <b>{tierLabel}</b></> : ""}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button type="button" style={seg(format === "xlsx")} onClick={() => { setFormat("xlsx"); setSavedAs(""); }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Excel (.xlsx)</div>
          </button>
          <button type="button" style={seg(format === "txt")} onClick={() => { setFormat("txt"); setSavedAs(""); }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Text (.txt)</div>
          </button>
        </div>

        {format === "xlsx" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 12.5 }}>
              <span style={{ fontWeight: 700 }}>Columns</span>
              <button type="button" className="pl-btn pl-btn-ghost" onClick={() => { setFields(new Set(EXPORT_FIELDS.map((f) => f.key))); setSavedAs(""); }}>All</button>
              <button type="button" className="pl-btn pl-btn-ghost" onClick={() => { setFields(new Set(DEFAULT_EXPORT_FIELDS)); setSavedAs(""); }}>Standard</button>
              <button type="button" className="pl-btn pl-btn-ghost" onClick={() => { setFields(new Set(["serials"])); setSavedAs(""); }}>Serial only</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px", marginTop: 10, fontSize: 13.5 }}>
              {EXPORT_FIELDS.map((f) => (
                <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={fields.has(f.key)} onChange={() => toggle(f.key)} /> {f.label}
                </label>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13.5, opacity: hasSerials ? 1 : 0.5 }}>
              <input type="checkbox" disabled={!hasSerials || serialOnly} checked={hasSerials && (perSerial || serialOnly)} onChange={(e) => setPerSerial(e.target.checked)} />
              One row per serial
            </label>
            {fields.size === 0 && <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>Tick at least one column.</div>}
          </>
        )}

        {error && <div style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>}
        {savedAs && <div style={{ marginTop: 12, color: "#15803d", fontSize: 13, fontWeight: 600 }}>Downloaded {savedAs}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button className="pl-btn pl-btn-ghost" onClick={onClose}>Close</button>
          <button className="pl-btn pl-btn-confirm" disabled={!canExport || busy} onClick={run}>
            <FileDown size={13} /> {busy ? "Preparing..." : total === 0 ? "Nothing to export" : "Export"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- auto-match ---------- */

const REASON_LABELS = { email: "email", phone: "phone", name: "name" };

function AutoMatchModal({ onClose, onLinked }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [onlyHigh, setOnlyHigh] = useState(false);
  const [pos, setPos] = useState(0);
  const [pick, setPick] = useState(0);
  const [handled, setHandled] = useState({}); // serial_id -> "linked" | "skipped"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.request("/serials/automatch/")
      .then((data) => setItems(data.results))
      .catch((e) => setError(e.message));
  }, []);

  const queue = (items || []).filter((r) => !onlyHigh || r.confidence === "high");
  const current = queue[pos];
  const linkedCount = Object.values(handled).filter((v) => v === "linked").length;
  const skippedCount = Object.values(handled).filter((v) => v === "skipped").length;
  const pendingHigh = queue.filter((r) => r.confidence === "high" && !handled[r.serial_id]);

  useEffect(() => { setPick(0); }, [pos, onlyHigh]);

  function close() {
    if (linkedCount > 0) onLinked();
    onClose();
  }

  async function postLinks(links) {
    setBusy(true);
    setError("");
    try {
      const res = await api.request("/serials/automatch/apply/", { method: "POST", body: { links } });
      const failed = new Set(res.skipped.map((x) => x.serial_id));
      setHandled((prev) => {
        const next = { ...prev };
        links.forEach((l) => { if (!failed.has(l.serial_id)) next[l.serial_id] = "linked"; });
        return next;
      });
      if (res.skipped.length) setError(res.skipped.map((x) => x.reason).join(" "));
      return res;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function linkCurrent() {
    if (!current || busy || handled[current.serial_id] === "linked") return;
    const res = await postLinks([{ serial_id: current.serial_id, plan_id: current.candidates[pick].plan_id }]);
    if (res && res.linked === 1) setPos((n) => n + 1);
  }

  function skipCurrent() {
    if (!current) return;
    setHandled((prev) => (prev[current.serial_id] ? prev : { ...prev, [current.serial_id]: "skipped" }));
    setPos((n) => n + 1);
  }

  async function linkAllHigh() {
    if (pendingHigh.length === 0) return;
    if (!window.confirm(`Link ${pendingHigh.length} high-confidence serial(s) to their top suggested plan?`)) return;
    await postLinks(pendingHigh.map((r) => ({ serial_id: r.serial_id, plan_id: r.candidates[0].plan_id })));
  }

  // keyboard: Enter = link, → / S = skip, ← = back, 1-3 = choose customer
  useEffect(() => {
    function onKey(e) {
      if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "Enter") { e.preventDefault(); linkCurrent(); }
      else if (e.key === "ArrowRight" || e.key.toLowerCase() === "s") skipCurrent();
      else if (e.key === "ArrowLeft") setPos((n) => Math.max(n - 1, 0));
      else if (["1", "2", "3"].includes(e.key) && current && current.candidates[Number(e.key) - 1]) setPick(Number(e.key) - 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const state = current ? handled[current.serial_id] : null;
  const finished = items && pos >= queue.length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }} onClick={close}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Auto-match serials</div>
          <button className="pl-btn pl-btn-ghost" onClick={close}><X size={14} /></button>
        </div>

        {!items && !error && <div style={{ marginTop: 18, fontSize: 13 }}>Finding matches...</div>}
        {error && <div style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>}

        {items && (
          <>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10, flexWrap: "wrap", fontSize: 12.5, color: "var(--ink-600)" }}>
              <span>{linkedCount} linked · {skippedCount} skipped</span>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={onlyHigh} onChange={(e) => { setOnlyHigh(e.target.checked); setPos(0); }} /> High confidence only
              </label>
              {pendingHigh.length > 0 && (
                <button className="pl-btn pl-btn-ghost" disabled={busy} onClick={linkAllHigh}>Link all {pendingHigh.length} high-confidence</button>
              )}
            </div>
            <div style={{ height: 6, background: "var(--grey-100, #f1f5f9)", borderRadius: 4, marginTop: 10 }}>
              <div style={{ height: 6, borderRadius: 4, background: "#15803d", width: `${queue.length ? Math.min(pos / queue.length, 1) * 100 : 0}%`, transition: "width .2s" }} />
            </div>
          </>
        )}

        {items && queue.length === 0 && <div style={{ marginTop: 20, fontSize: 13.5, color: "var(--ink-600)" }}>No suggestions right now.</div>}

        {finished && queue.length > 0 && (
          <div style={{ marginTop: 20, textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>All done</div>
            <div style={{ fontSize: 13.5, color: "var(--ink-600)", marginTop: 6 }}>{linkedCount} linked, {skippedCount} skipped.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
              <button className="pl-btn pl-btn-ghost" onClick={() => setPos(0)}>Review again</button>
              <button className="pl-btn pl-btn-confirm" onClick={close}>Close</button>
            </div>
          </div>
        )}

        {current && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, color: "var(--ink-500)" }}>
              <span>{pos + 1} of {queue.length}</span>
              <span style={{ padding: "3px 8px", borderRadius: 12, fontWeight: 600, background: current.confidence === "high" ? "#dcfce7" : "#fef3c7", color: current.confidence === "high" ? "#15803d" : "#b45309" }}>
                {current.confidence === "high" ? "High confidence" : "Likely — check carefully"}
              </span>
            </div>

            <div style={{ marginTop: 10, padding: 14, border: "1px solid var(--line)", borderRadius: 12, background: "var(--grey-100, #f8fafc)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-500)", textTransform: "uppercase", letterSpacing: "0.4px" }}>Serial from the sheet</div>
              <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 20, marginTop: 2 }}>{current.serial_number}</div>
              <div style={{ fontSize: 14, marginTop: 4 }}>{current.client_name || "—"}</div>
              <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 4 }}>
                {current.current_code ? "Has an activation code" : "No activation code yet"}
              </div>
            </div>

            <div style={{ textAlign: "center", color: "var(--ink-400)", margin: "8px 0" }}>↓ looks like</div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {current.candidates.map((c, i) => (
                <div
                  key={c.plan_id}
                  onClick={() => setPick(i)}
                  style={{ cursor: "pointer", padding: 12, borderRadius: 12, border: `2px solid ${pick === i ? "#15803d" : "var(--line)"}`, background: pick === i ? "#f0fdf4" : "#fff" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5 }}>{c.customer_name}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-600)", whiteSpace: "nowrap" }}>
                      {Math.round(c.score * 100)}% · {c.reasons.map((x) => REASON_LABELS[x]).join(" + ")}
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--ink-600)", marginTop: 3 }}>
                    Plan: {c.plan_name}{c.plan_has_serials ? ` · already has ${c.plan_has_serials} serial(s)` : " · no serial yet"}
                  </div>
                  <Link to={`/customers/${c.customer_id}`} target="_blank" onClick={(e) => e.stopPropagation()} style={{ fontSize: 12, color: "var(--blue-700)" }}>
                    Open customer ↗
                  </Link>
                </div>
              ))}
            </div>

            {state && (
              <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: state === "linked" ? "#15803d" : "var(--ink-500)" }}>
                {state === "linked" ? "Already linked." : "You skipped this one earlier."}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
              <button className="pl-btn pl-btn-ghost" disabled={pos === 0} onClick={() => setPos((n) => Math.max(n - 1, 0))}><ChevronLeft size={14} /> Back</button>
              <div style={{ flex: 1 }} />
              <button className="pl-btn pl-btn-ghost" onClick={skipCurrent}>Skip</button>
              <button className="pl-btn pl-btn-confirm" disabled={busy || state === "linked"} onClick={linkCurrent}>
                <Check size={13} /> {busy ? "Linking..." : "Link"}
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--ink-400)", marginTop: 8 }}>Keys: Enter = link · → or S = skip · ← = back · 1/2/3 = pick customer</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- page ---------- */

export default function PlanManagement() {
  const [tab, setTab] = useState("plans");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [expiringWithin, setExpiringWithin] = useState("");
  const [ordering, setOrdering] = useState("");
  const [serialStatus, setSerialStatus] = useState("");
  const [completion, setCompletion] = useState("");
  const [lastPayment, setLastPayment] = useState("");
  const [codeStatus, setCodeStatus] = useState("");
  const [linkStatus, setLinkStatus] = useState("");
  const [codeTier, setCodeTier] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAutoMatch, setShowAutoMatch] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingReminders, setSendingReminders] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const isFirstSearchEffect = useRef(true);
  const isFirstFilterEffect = useRef(true);

  const fetchPage = useCallback(
    (pageNum, triggerType) => {
      const myRequestId = ++requestIdRef.current;

      if (triggerType === "search") {
        setSearching(true);
        setFiltering(false);
      } else if (triggerType === "filter" || triggerType === "tab") {
        setFiltering(true);
        setSearching(false);
      }

      let req;
      if (tab === "plans") {
        req = api.request("/plans/list/", {
          params: {
            search,
            expiring_within: expiringWithin || undefined,
            ordering: ordering || undefined,
            serial_status: serialStatus || undefined,
            completion: completion || undefined,
            last_payment: lastPayment || undefined,
            code_tier: codeTier || undefined,
            page: pageNum,
            limit: PAGE_SIZE,
          },
        });
      } else {
        req = api.request("/serials/", {
          params: {
            search,
            linked_only: linkStatus === "linked" ? "true" : undefined,
            unlinked_only: linkStatus === "unlinked" ? "true" : undefined,
            code_status: codeStatus || undefined,
            page: pageNum,
            limit: PAGE_SIZE,
          },
        });
      }

      req
        .then((data) => {
          if (myRequestId !== requestIdRef.current) return;
          setRows(data.results);
          setTotal(data.total);
          setHasMore(data.has_more);
          setError("");
        })
        .catch((e) => {
          if (myRequestId !== requestIdRef.current) return;
          setError(e.message);
        })
        .finally(() => {
          if (myRequestId !== requestIdRef.current) return;
          setLoading(false);
          setSearching(false);
          setFiltering(false);
        });
    },
    [tab, search, expiringWithin, ordering, serialStatus, completion, lastPayment, codeStatus, linkStatus, codeTier]
  );

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchPage(1, "tab_initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    setSearching(true);
    setFiltering(false);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, "search");
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    setSearching(false);
    setPage(1);
    fetchPage(1, "filter");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiringWithin, ordering, serialStatus, completion, lastPayment, codeStatus, linkStatus, codeTier]);

  useEffect(() => {
    setSelectedIds(new Set());
    setExpandedId(null);
  }, [tab, expiringWithin, page]);

  function goToPage(n) {
    if (n < 1) return;
    setPage(n);
    setFiltering(true);
    fetchPage(n, "filter");
  }

  function toggleSelect(id, e) {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedIds(new Set(rows.map((r) => r.id)));
  }

  async function sendReminders() {
    if (selectedIds.size === 0) return;
    setSendingReminders(true);
    try {
      const result = await api.request("/plans/send-expiry-reminders/", {
        method: "POST",
        body: { plan_ids: Array.from(selectedIds) },
      });
      alert(`Sent ${result.sent} reminder(s)${result.failed ? `, ${result.failed} failed` : ""}.`);
      setSelectedIds(new Set());
    } catch (e) {
      alert(e.message);
    } finally {
      setSendingReminders(false);
    }
  }

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const planColumns = expiringWithin ? 10 : 9;

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 8px" }}>
      <style>{`
        @keyframes pl-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="pl-page-title">Plan Management</div>
      <div className="pl-page-sub">
        Every plan, its payment progress, device serial and activation code — replaces the manual GNSS codes sheet.
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
        <button className={`pl-btn ${tab === "plans" ? "pl-btn-confirm" : "pl-btn-ghost"}`} onClick={() => setTab("plans")}>Plans</button>
        <button className={`pl-btn ${tab === "serials" ? "pl-btn-confirm" : "pl-btn-ghost"}`} onClick={() => setTab("serials")}>Serials</button>
        {tab === "plans" && (
          <button className="pl-btn pl-btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setShowExport(true)}>
            <FileDown size={13} /> Export
          </button>
        )}
        <button className="pl-btn pl-btn-ghost" style={tab === "plans" ? undefined : { marginLeft: "auto" }} onClick={() => setShowAutoMatch(true)}>
          <Key size={13} /> Auto-match
        </button>
        <button className="pl-btn pl-btn-ghost" onClick={() => setShowImport(true)}>
          <FileDown size={13} style={{ transform: "rotate(180deg)" }} /> Import Excel
        </button>
      </div>

      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          total={total}
          codeTier={codeTier}
          filters={{
            search: search || undefined,
            expiring_within: expiringWithin || undefined,
            ordering: ordering || undefined,
            serial_status: serialStatus || undefined,
            completion: completion || undefined,
            last_payment: lastPayment || undefined,
            code_tier: codeTier || undefined,
          }}
        />
      )}

      {showAutoMatch && (
        <AutoMatchModal
          onClose={() => setShowAutoMatch(false)}
          onLinked={() => fetchPage(1, "filter")}
        />
      )}

      {showImport && (
        <ImportSerialsModal
          onClose={() => setShowImport(false)}
          onImported={() => fetchPage(1, "filter")}
        />
      )}

      <div className="pl-customers-toolbar" style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1.5px solid var(--line)", borderRadius: 10, padding: "10px 12px", width: "100%", position: "relative" }}>
          <Search size={14} color="var(--ink-400)" />
          <input
            style={{ border: 0, outline: 0, fontSize: 13.5, width: "100%", paddingRight: searching ? 26 : undefined }}
            placeholder={tab === "plans" ? "Search customer, equipment, serial, or client name" : "Search serial, client name"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searching && (
            <div style={{ position: "absolute", right: 12 }}>
              <SpinIcon size={14} />
            </div>
          )}
        </div>

        {tab === "plans" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-start", width: "100%" }}>
            <FilterSelect label="Sort By" value={ordering} onChange={setOrdering} options={ORDERING_OPTIONS} />
            <FilterSelect label="Plan Status" value={completion} onChange={setCompletion} options={COMPLETION_FILTERS} />
            <FilterSelect label="Last Payment" value={lastPayment} onChange={setLastPayment} options={LAST_PAYMENT_FILTERS} />
            <FilterSelect label="Code Eligibility" value={codeTier} onChange={setCodeTier} options={CODE_TIER_FILTERS} />
            <FilterSelect label="Code Expiry" value={expiringWithin} onChange={setExpiringWithin} options={EXPIRY_FILTERS} />
            <FilterSelect label="Serial Status" value={serialStatus} onChange={setSerialStatus} options={SERIAL_STATUS_FILTERS} />
          </div>
        )}

        {tab !== "plans" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%" }}>
            <FilterSelect label="Link Status" value={linkStatus} onChange={setLinkStatus} options={LINK_STATUS_FILTERS} />
            <FilterSelect label="Activation Code" value={codeStatus} onChange={setCodeStatus} options={CODE_STATUS_FILTERS} />
          </div>
        )}
      </div>

      {tab === "plans" && expiringWithin && rows.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, padding: "10px 14px", background: "var(--amber-100, #fef3c7)", borderRadius: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.size} selected</span>
          <button className="pl-btn pl-btn-ghost" onClick={selectAll}>Select all on this page ({rows.length})</button>
          <button className="pl-btn pl-btn-confirm" disabled={selectedIds.size === 0 || sendingReminders} onClick={sendReminders}>
            <Send size={13} /> {sendingReminders ? "Sending..." : "Send expiry reminder"}
          </button>
        </div>
      )}

      {loading && (
        <TableSkeleton columns={tab === "plans" ? planColumns : 5} />
      )}

      {!loading && error && (
        <div className="pl-empty" style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <span>Something went wrong loading this page. Please try again.</span>
          <button className="pl-btn pl-btn-ghost" onClick={() => fetchPage(page, "filter")}>Try again</button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && !filtering && !searching && (
        <div className="pl-empty">Nothing here.</div>
      )}

      {!loading && rows.length > 0 && (
        <div style={{ position: "relative" }}>
          {filtering && (
            <div
              style={{
                position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.55)",
                zIndex: 10, borderRadius: 8, display: "flex", alignItems: "flex-start",
                justifyContent: "center", paddingTop: 40, backdropFilter: "blur(1px)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", background: "#fff", borderRadius: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", fontSize: 13, fontWeight: 500, color: "var(--ink-700, #334155)" }}>
                <SpinIcon size={16} /> Updating results...
              </div>
            </div>
          )}

          <div style={{ opacity: filtering ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
            {tab === "plans" && (
              <PlansTable
                rows={rows}
                expiringWithin={expiringWithin}
                colSpan={planColumns}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                expandedId={expandedId}
                onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
                onChanged={() => fetchPage(page, "filter")}
              />
            )}
            {tab === "serials" && <SerialsTable rows={rows} />}
          </div>
        </div>
      )}

      {!loading && total > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, flexWrap: "wrap", gap: 10 }}>
          <div className="pl-meta">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="pl-btn pl-btn-ghost" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
              <ChevronLeft size={14} /> Previous
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-600)" }}>Page {page} of {totalPages}</span>
            <button className="pl-btn pl-btn-ghost" disabled={!hasMore} onClick={() => goToPage(page + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- tables ---------- */

function PlansTable({ rows, expiringWithin, colSpan, selectedIds, onToggleSelect, expandedId, onToggleExpand, onChanged }) {
  const th = { padding: "12px 14px", fontWeight: 600 };
  return (
    <div style={{ marginTop: 16, border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100, fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: "var(--grey-100, #f8fafc)", textAlign: "left", color: "var(--ink-500, #64748b)" }}>
              {expiringWithin && <th style={{ padding: "12px", width: 36 }}></th>}
              <th style={{ padding: "12px", width: 32 }}></th>
              <th style={th}>Customer</th>
              <th style={th}>Equipment</th>
              <th style={th}>Total</th>
              <th style={th}>Progress</th>
              <th style={th}>Last payment</th>
              <th style={th}>Duration</th>
              <th style={th}>Serials</th>
              <th style={th}>Code expiry</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((plan) => {
              const m = plan.metrics || {};
              const isExpanded = expandedId === plan.id;
              const mostUrgentDays = (plan.serials || []).reduce((min, s) => {
                if (s.days_until_expiry === null || s.days_until_expiry === undefined) return min;
                return min === null ? s.days_until_expiry : Math.min(min, s.days_until_expiry);
              }, null);

              return (
                <React.Fragment key={plan.id}>
                  <tr
                    onClick={() => onToggleExpand(plan.id)}
                    style={{
                      borderTop: "1px solid var(--line)",
                      cursor: "pointer",
                      background: isExpanded ? "var(--blue-50, #f8fafc)" : "transparent",
                      transition: "background 0.15s ease",
                    }}
                  >
                    {expiringWithin && (
                      <td style={{ padding: "12px" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(plan.id)}
                          onChange={(e) => onToggleSelect(plan.id, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td style={{ padding: "12px", color: "var(--ink-400)" }}>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </td>
                    <td style={{ padding: "12px 14px", fontWeight: 600 }}>
                      <Link
                        to={`/customers/${plan.customer}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "var(--blue-900, #1e3a8a)", textDecoration: "none" }}
                      >
                        {plan.customer_name}
                      </Link>
                    </td>
                    <td style={{ padding: "12px 14px", color: "var(--ink-700)" }}>{plan.display_name || plan.equipment_name}</td>
                    <td style={{ padding: "12px 14px", fontWeight: 500 }}>{naira(plan.total_price)}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <ProgressBar percent={m.percent ?? 0} />
                      {m.is_completed && (
                        <div style={{ fontSize: 11, color: "#15803d", fontWeight: 600, marginTop: 3 }}>Completed</div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      {m.last_payment_at ? (
                        <>
                          <div style={{ fontWeight: 500 }}>{formatDate(m.last_payment_at)}</div>
                          <div style={{ fontSize: 11.5, color: "var(--ink-500)" }}>{agoLabel(m.last_payment_at)}</div>
                        </>
                      ) : (
                        <span style={{ color: "var(--ink-400)" }}>Never</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap", color: "var(--ink-700)" }}>
                      {durationLabel(m)}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      {(plan.serials || []).length === 0 ? (
                        <span style={{ padding: "2px 8px", borderRadius: 6, background: "#fef3c7", color: "#b45309", fontSize: 12, fontWeight: 500 }}>
                          Unlinked
                        </span>
                      ) : (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {plan.serials.map((s) => (
                            <span
                              key={s.id}
                              style={{
                                fontFamily: "monospace", fontSize: 12.5, background: "var(--green-100, #dcfce7)",
                                padding: "2px 6px", borderRadius: 4, color: "var(--green-700, #15803d)", fontWeight: 600,
                              }}
                            >
                              {s.serial_number}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", fontWeight: 600, color: expiryColor(mostUrgentDays) }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                        {mostUrgentDays !== null && mostUrgentDays <= 7 && <AlertTriangle size={13} />}
                        <span>{expiryLabel(mostUrgentDays)}</span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: "var(--blue-50, #f8fafc)", borderTop: "none" }}>
                      <td colSpan={colSpan} style={{ padding: "16px 20px", borderBottom: "1px solid var(--line)" }}>
                        <PlanDetailPanel plan={plan} onChanged={onChanged} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SerialsTable({ rows }) {
  return (
    <div style={{ marginTop: 16, border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: "var(--grey-100, #f8fafc)", textAlign: "left", color: "var(--ink-500)" }}>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Serial number</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Client name (sheet)</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Linked customer</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Code</th>
              <th style={{ padding: "12px 14px", fontWeight: 600 }}>Expiry</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={{ padding: "12px 14px", fontWeight: 700, fontFamily: "monospace" }}>{s.serial_number}</td>
                <td style={{ padding: "12px 14px", color: "var(--ink-700)" }}>{s.client_name || "—"}</td>
                <td style={{ padding: "12px 14px" }}>
                  {s.customer_id ? (
                    <Link to={`/customers/${s.customer_id}`} style={{ color: "var(--blue-700)", textDecoration: "none" }}>
                      {s.customer_name}
                    </Link>
                  ) : (
                    <span style={{ color: "var(--ink-400)" }}>Not linked</span>
                  )}
                </td>
                <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 12.5, color: s.current_code ? "var(--ink-700)" : "var(--ink-400)" }}>
                  {s.current_code || "No code"}
                </td>
                <td style={{ padding: "12px 14px" }}>{s.code_expiry ? formatDate(s.code_expiry) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ---------- expanded panel ---------- */

const STATUS_STYLES = {
  confirmed: { bg: "#dcfce7", fg: "#15803d" },
  pending: { bg: "#fef3c7", fg: "#b45309" },
  rejected: { bg: "#fee2e2", fg: "#b91c1c" },
};

function StatCard({ label, value, accent }) {
  return (
    <div style={{ flex: "1 1 130px", background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3, color: accent || "var(--ink-900)" }}>{value}</div>
    </div>
  );
}

function PlanDetailPanel({ plan, onChanged }) {
  const m = plan.metrics || {};
  const history = plan.payment_history || [];
  const contact = plan.contact || { email: null, phones: [] };

  const [showAllHistory, setShowAllHistory] = useState(false);
  const [serialInput, setSerialInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [editingSerialId, setEditingSerialId] = useState(null);
  const [editForm, setEditForm] = useState({ current_code: "", code_expiry: "" });
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    equipment_name: plan.needs_review ? "" : plan.equipment_name,
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const suggestDebounce = useRef(null);
  const autocompleteRef = useRef(null);

  const summaryText = buildSummary(plan);
  const visibleHistory = showAllHistory ? history : history.slice(0, 5);

  useEffect(() => {
    function onClickOutside(e) {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(suggestDebounce.current), []);

  function handleSerialInputChange(value) {
    setSerialInput(value);
    setLinkError("");
    clearTimeout(suggestDebounce.current);
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestDebounce.current = setTimeout(async () => {
      try {
        const data = await api.request("/serials/search/", { params: { q: value.trim() } });
        setSuggestions(data?.results || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
  }

  function pickSuggestion(serial) {
    setSerialInput(serial.serial_number);
    setShowSuggestions(false);
  }

  async function handleLinkSerial() {
    if (!serialInput.trim()) return;
    setLinking(true);
    setLinkError("");
    try {
      await api.request(`/plans/${plan.id}/link-serial/`, {
        method: "POST",
        body: { serial_number: serialInput.trim() },
      });
      setSerialInput("");
      setSuggestions([]);
      setShowSuggestions(false);
      onChanged();
    } catch (e) {
      setLinkError(e.message);
    } finally {
      setLinking(false);
    }
  }

  async function handleUnlink(serialId) {
    if (!window.confirm("Unlink this serial from the plan?")) return;
    try {
      await api.request(`/serials/${serialId}/unlink/`, { method: "POST" });
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  }

  function startEditSerial(s) {
    setEditingSerialId(s.id);
    setEditForm({ current_code: s.current_code || "", code_expiry: s.code_expiry || "" });
  }

  async function saveSerialEdit(serialId) {
    setSaving(true);
    try {
      await api.request(`/serials/${serialId}/`, { method: "PATCH", body: editForm });
      setEditingSerialId(null);
      onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function savePlanEdit() {
    setSavingPlan(true);
    try {
      await api.updatePlan(plan.id, planForm);
      setEditingPlan(false);
      onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingPlan(false);
    }
  }

  const sectionTitle = {
    fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--ink-500)", letterSpacing: "0.5px",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Summary */}
      {summaryText && (
        <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px", fontSize: 13, lineHeight: 1.55, color: "var(--ink-700)" }}>
          {summaryText}
        </div>
      )}

      {/* Customer contact */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={sectionTitle}>Customer Contact</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px", display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px" }}>
            <Mail size={15} color="var(--ink-400)" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Email</div>
              {contact.email ? (
                <a href={`mailto:${contact.email}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--blue-700)", textDecoration: "none", wordBreak: "break-all" }}>
                  {contact.email}
                </a>
              ) : (
                <span style={{ fontSize: 13.5, color: "var(--ink-400)" }}>Not on file</span>
              )}
            </div>
          </div>
          <div style={{ flex: "1 1 240px", display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px" }}>
            <Phone size={15} color="var(--ink-400)" />
            <div>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {contact.phones.length > 1 ? "Phone numbers" : "Phone"}
              </div>
              {contact.phones.length ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {contact.phones.map((p) => (
                    <a key={p} href={`tel:${p}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)", textDecoration: "none", fontFamily: "monospace" }}>
                      {p}
                    </a>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 13.5, color: "var(--ink-400)" }}>Not on file</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment overview */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={sectionTitle}>Payment Overview</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard label="Total" value={naira(m.total_price)} />
          <StatCard label="Paid" value={naira(m.total_paid)} accent="#15803d" />
          <StatCard label="Left" value={naira(m.balance)} accent={m.balance > 0 ? "#b45309" : "#15803d"} />
          <StatCard label="Last payment" value={m.last_payment_at ? `${formatDate(m.last_payment_at)} (${agoLabel(m.last_payment_at)})` : "Never"} />
          <StatCard label={m.is_completed ? "Completed in" : "Running for"} value={durationLabel(m).replace(" so far", "")} />
        </div>
        <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px" }}>
          <ProgressBar percent={m.percent ?? 0} width={320} height={10} />
        </div>
      </div>

      {/* Payment history */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={sectionTitle}>Payment History ({history.length})</div>
        {history.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ink-500)" }}>No payments recorded for this plan.</div>
        ) : (
          <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--ink-500)", background: "#f8fafc" }}>
                  <th style={{ padding: "8px 12px", fontWeight: 600 }}>Date</th>
                  <th style={{ padding: "8px 12px", fontWeight: 600 }}>Amount</th>
                  <th style={{ padding: "8px 12px", fontWeight: 600 }}>Status</th>
                  <th style={{ padding: "8px 12px", fontWeight: 600 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {visibleHistory.map((p) => {
                  const st = STATUS_STYLES[p.status] || STATUS_STYLES.pending;
                  return (
                    <tr key={p.id} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: "8px 12px" }}>{p.date ? formatDate(p.date) : "—"}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{naira(p.amount)}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: 10, background: st.bg, color: st.fg, fontSize: 11.5, fontWeight: 600, textTransform: "capitalize" }}>
                          {p.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--ink-600)" }}>{p.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {history.length > 5 && (
              <button
                className="pl-btn pl-btn-ghost"
                style={{ width: "100%", borderRadius: 0, borderTop: "1px solid var(--line)" }}
                onClick={() => setShowAllHistory((v) => !v)}
              >
                {showAllHistory ? "Show less" : `Show all ${history.length}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Serials */}
      {(plan.serials || []).length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={sectionTitle}>Linked Device Serials & Activation Codes</div>
          {plan.serials.map((s) => {
            const d = s.days_until_expiry;
            return (
              <div
                key={s.id}
                style={{ background: "#ffffff", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
              >
                {editingSerialId === s.id ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ flex: "1 1 160px" }}>
                      <label style={{ fontSize: 11, display: "block", color: "var(--ink-500)", marginBottom: 2 }}>Activation Code</label>
                      <input
                        value={editForm.current_code}
                        onChange={(e) => setEditForm((f) => ({ ...f, current_code: e.target.value }))}
                        placeholder="e.g. 8492-4910"
                        style={{ width: "100%", padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid var(--line)" }}
                      />
                    </div>
                    <div style={{ flex: "0 0 140px" }}>
                      <label style={{ fontSize: 11, display: "block", color: "var(--ink-500)", marginBottom: 2 }}>Code Expiry</label>
                      <input
                        type="date"
                        value={editForm.code_expiry}
                        onChange={(e) => setEditForm((f) => ({ ...f, code_expiry: e.target.value }))}
                        style={{ width: "100%", padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid var(--line)" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
                      <button className="pl-btn pl-btn-confirm" disabled={saving} onClick={() => saveSerialEdit(s.id)}>
                        <Check size={13} /> Save
                      </button>
                      <button className="pl-btn pl-btn-ghost" onClick={() => setEditingSerialId(null)}>
                        <X size={13} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Smartphone size={14} color="var(--ink-400)" />
                        <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13.5, color: "var(--ink-900)" }}>{s.serial_number}</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", padding: "4px 8px", borderRadius: 6, border: "1px solid #f1f5f9" }}>
                        <Key size={13} color="var(--ink-400)" />
                        <span style={{ fontSize: 12, color: "var(--ink-500)" }}>Code:</span>
                        <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "var(--ink-800)" }}>{s.current_code || "—"}</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", padding: "4px 8px", borderRadius: 6, border: "1px solid #f1f5f9" }}>
                        <Calendar size={13} color="var(--ink-400)" />
                        <span style={{ fontSize: 12, color: "var(--ink-500)" }}>{d !== null && d !== undefined && d < 0 ? "Expired:" : "Expires:"}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-800)" }}>
                          {s.code_expiry ? formatDate(s.code_expiry) : "—"}
                        </span>
                        {d !== null && d !== undefined && (
                          <span style={{ fontSize: 12, fontWeight: 700, color: expiryColor(d) }}>· {expiryLabel(d)}</span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="pl-btn pl-btn-ghost" onClick={() => startEditSerial(s)} style={{ padding: "4px 10px", fontSize: 12 }}>
                        <Edit2 size={12} /> Edit Code
                      </button>
                      <button className="pl-btn pl-btn-reject" onClick={() => handleUnlink(s.id)} style={{ padding: "4px 10px", fontSize: 12 }}>
                        <Unlink size={12} /> Unlink
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "var(--amber-700)", background: "#fffbeb", padding: "8px 12px", borderRadius: 6, border: "1px solid #fef3c7" }}>
          No serial numbers are currently linked to this plan.
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", paddingTop: 4 }}>
        <div ref={autocompleteRef} style={{ position: "relative", flex: "1 1 240px", maxWidth: 400 }}>
          <input
            placeholder="Search by serial number or client name to link..."
            value={serialInput}
            onChange={(e) => handleSerialInputChange(e.target.value)}
            onFocus={() => serialInput.trim().length >= 2 && setShowSuggestions(true)}
            style={{ width: "100%", padding: "7px 10px", fontSize: 13, borderRadius: 6, border: "1px solid var(--line)", background: "#ffffff" }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#ffffff",
                border: "1px solid var(--line)", borderRadius: 8, marginTop: 4,
                boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                maxHeight: 200, overflowY: "auto",
              }}
            >
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => pickSuggestion(s)}
                  style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--line)" }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div style={{ fontWeight: 700, fontFamily: "monospace" }}>{s.serial_number}</div>
                  {s.client_name && <div className="pl-meta" style={{ fontSize: 11 }}>{s.client_name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <button className="pl-btn pl-btn-confirm" disabled={linking || !serialInput.trim()} onClick={handleLinkSerial}>
          <Plus size={13} /> {linking ? "Linking..." : "Link Serial"}
        </button>

        <button className="pl-btn pl-btn-ghost" onClick={() => setEditingPlan((v) => !v)}>
          <Edit2 size={13} /> Edit Plan Details
        </button>

        <button className="pl-btn pl-btn-ghost" onClick={() => exportPlanPdf(plan, summaryText)}>
          <FileDown size={13} /> Save as PDF
        </button>
      </div>

      {linkError && <div style={{ color: "var(--red-700)", fontSize: 12.5 }}>{linkError}</div>}

      {editingPlan && (
        <div style={{ marginTop: 6, padding: 12, background: "#ffffff", border: "1px solid var(--line)", borderRadius: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: 11, display: "block", color: "var(--ink-500)", marginBottom: 2 }}>Equipment Name</label>
            <input
              value={planForm.equipment_name}
              placeholder={plan.needs_review ? "Enter the real equipment name" : ""}
              onChange={(e) => setPlanForm((f) => ({ ...f, equipment_name: e.target.value }))}
            />
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
            <button className="pl-btn pl-btn-confirm" disabled={savingPlan} onClick={savePlanEdit}>Save Equipment</button>
            <button className="pl-btn pl-btn-ghost" onClick={() => setEditingPlan(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}